import React, { memo, useEffect, useRef, useState } from 'react';
import { CircularProgress, InputBase, Theme, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { LocationOn } from '@material-ui/icons';
import { ColumnDef, getColumnOptions } from '../activity-import-columns';
import { GeocodeStatus } from '../activity-import-types';

const useStyles = makeStyles((theme: Theme) => ({
  cell: {
    height: 36,
    padding: '0 10px',
    fontSize: '13px',
    color: '#333333',
    borderRight: `1px solid ${theme.color.grey.border}`,
    borderBottom: `1px solid ${theme.color.grey.border}`,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'text',
    boxSizing: 'border-box',
    backgroundColor: 'inherit',
  },
  error: {
    color: theme.palette.error.main,
    boxShadow: `inset 0 -2px 0 ${theme.palette.error.main}`,
  },
  disabled: {
    backgroundColor: '#FAFAFA',
    color: '#B5B5B5',
    cursor: 'default',
  },
  readOnly: {
    cursor: 'default',
  },
  placeholder: {
    color: theme.color.grey.neutral200,
  },
  editing: {
    padding: 0,
    backgroundColor: '#ffffff',
    boxShadow: `inset 0 0 0 2px ${theme.color.secondary.main900}`,
  },
  input: {
    fontSize: '13px',
    width: '100%',
    height: '100%',
    padding: '0 10px',
  },
  select: {
    fontSize: '13px',
    width: '100%',
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: '0 6px',
    color: '#333333',
  },
  addressContent: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    '& > * + *': {
      marginLeft: 4,
    },
  },
  addressText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

interface Props {
  rowId: string;
  column: ColumnDef;
  value: string;
  error?: string;
  applicable: boolean;
  inapplicableReason?: string;
  placeholder?: string;
  readOnly: boolean;
  geocodeStatus?: GeocodeStatus;
  programNames: string[];
  reportTypeNames: string[];
  className?: string;
  onChange: (rowId: string, key: string, value: string) => void;
  onAddressClick: (rowId: string) => void;
}

export const GridCell = memo(
  ({
    rowId,
    column,
    value,
    error,
    applicable,
    inapplicableReason,
    placeholder,
    readOnly,
    geocodeStatus,
    programNames,
    reportTypeNames,
    className: extraClassName,
    onChange,
    onAddressClick,
  }: Props) => {
    const classes = useStyles();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const selectRef = useRef<HTMLSelectElement>(null);

    useEffect(() => {
      if (editing && selectRef.current) selectRef.current.focus();
    }, [editing]);

    const style: React.CSSProperties = { width: column.width, minWidth: column.width, maxWidth: column.width };

    if (!applicable) {
      const empty = <td className={[classes.cell, classes.disabled, extraClassName].join(' ')} style={style} />;
      return inapplicableReason ? (
        <Tooltip title={inapplicableReason} placement='top' enterDelay={400}>
          {empty}
        </Tooltip>
      ) : (
        empty
      );
    }

    const className = [
      classes.cell,
      error && !editing ? classes.error : '',
      readOnly ? classes.readOnly : '',
      editing ? classes.editing : '',
      extraClassName,
    ].join(' ');

    const startEditing = () => {
      if (readOnly) return;
      if (column.kind === 'address') {
        onAddressClick(rowId);
        return;
      }
      setDraft(value);
      setEditing(true);
    };

    const commit = (next: string) => {
      setEditing(false);
      if (next.trim() !== value) onChange(rowId, column.key, next.trim());
    };

    const options = getColumnOptions(column, programNames, reportTypeNames);

    let content: React.ReactNode =
      value || (placeholder ? <span className={classes.placeholder}>{placeholder}</span> : '');
    if (editing && options) {
      const displayOptions = value && !options.includes(value) ? [value, ...options] : options;
      content = (
        <select
          ref={selectRef}
          className={classes.select}
          value={value}
          onChange={(e) => commit(e.target.value)}
          onBlur={() => setEditing(false)}
        >
          <option value=''>{column.kind === 'bool' ? '(default)' : ''}</option>
          {displayOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    } else if (editing) {
      content = (
        <InputBase
          autoFocus
          className={classes.input}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit(draft);
            if (e.key === 'Escape') setEditing(false);
          }}
        />
      );
    } else if (column.kind === 'address' && value) {
      content = (
        <span className={classes.addressContent}>
          {geocodeStatus === 'pending' && <CircularProgress size={12} />}
          {geocodeStatus === 'resolved' && <LocationOn style={{ fontSize: 14, color: '#4caf50' }} />}
          <span className={classes.addressText}>{value}</span>
        </span>
      );
    }

    const cell = (
      <td className={className} style={style} onClick={editing ? undefined : startEditing}>
        {content}
      </td>
    );

    const tooltip = error && !editing ? error : !editing && !value ? column.help : undefined;
    return tooltip ? (
      <Tooltip title={tooltip} placement='top' enterDelay={error ? 100 : 600}>
        {cell}
      </Tooltip>
    ) : (
      cell
    );
  }
);
